import { Link } from 'react-router';
import { canOpenView, doctorPath, patientPath, type ViewKey } from '@/domain';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';

export interface ClientLinkProps {
  /** Which of the two records the name belongs to. */
  kind: 'doctor' | 'patient';
  /** The record's id. Null or absent on a case nobody linked, and for a reader without seeIdentity. */
  id: string | null | undefined;
  /** The name as the case carries it. Null or absent for a reader without seeIdentity. */
  name: string | null | undefined;
}

/** The screen each profile belongs to, and where it opens. */
const PROFILE: Record<
  ClientLinkProps['kind'],
  { view: ViewKey; path: (id: string) => string }
> = {
  doctor: { view: 'doctors', path: doctorPath },
  patient: { view: 'patients', path: patientPath },
};

/**
 * A doctor's or a patient's name where a case shows it: the order drawer, the
 * case record and the case screen's details. The request of 6 October 2026
 * (second), item 4: a clear path from a case to its doctor and its patient.
 *
 * A link to the profile for a reader who holds the matching screen, when the
 * case is linked to a record. Otherwise the plain name, as it always was: no
 * link is offered that the shell would send straight home. A name the reader
 * may not see reads Hidden, as it does on every list.
 *
 * Not for a table cell. A row is itself the thing to press, and a link inside
 * it would be a second target the size of a word.
 */
export function ClientLink({ kind, id, name }: ClientLinkProps) {
  const session = useSession();

  if (name == null || name === '') return <>{strings.table.hidden}</>;

  const profile = PROFILE[kind];
  if (id == null || id === '' || !canOpenView(session.data?.views ?? [], profile.view)) {
    return <>{name}</>;
  }
  return <Link to={profile.path(id)}>{name}</Link>;
}
